/**
 * @file CustomerEmailInlineForm.tsx
 * @description Инлайн-добавление e-mail клиента прямо в форме заказа
 * (customers.md F15, решение пользователя 2026-09-21).
 *
 * Без адреса письмо с согласованием отправлять некуда, и главная кнопка формы
 * заблокирована. Гонять менеджера в карточку клиента ради одного поля — терять
 * набранный заказ, поэтому адрес сохраняется отсюда (`PATCH /admin/customers/:id`)
 * и возвращается в форму уже обновлённым клиентом: один источник правды —
 * карточка клиента, а не копия адреса в заказе.
 */

import { useState } from "react";

import { ApiError } from "../../lib/api-client";
import { customers, type CustomerDto } from "../../lib/api-customers";
import {
  CUSTOMER_EMAIL_HINT,
  validateCustomerEmail,
} from "../../lib/customer-email";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";

interface Props {
  customerId: string;
  /** Клиент с сохранённым адресом — форма подставит его вместо прежнего. */
  onSaved: (customer: CustomerDto) => void;
}

export function CustomerEmailInlineForm({ customerId, onSaved }: Props) {
  const notify = useNotify();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    const problem = validateCustomerEmail(email);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await customers.update(customerId, { email: email.trim() });
      notify.success("E-mail сохранён в карточке клиента");
      onSaved(saved);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Не удалось сохранить e-mail";
      setError(message);
      notify.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="o-customer-email">Добавить e-mail</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="o-customer-email"
          type="email"
          className="min-h-12 flex-1"
          value={email}
          disabled={saving}
          placeholder="ivanov@mail.ru"
          autoComplete="email"
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
          }}
        />
        <Button className="min-h-12" disabled={saving} onClick={() => void save()}>
          {saving ? "Сохраняем…" : "Сохранить"}
        </Button>
      </div>
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-on-surface-variant">
          {CUSTOMER_EMAIL_HINT}. Адрес сохранится в карточке клиента.
        </p>
      )}
    </div>
  );
}
